import React from "react";
import { Button } from "../ui/button";
import { useToast } from "../ui/use-toast";
import { Shuffle, Undo2, Send } from "lucide-react";
import {
    isGuessCorrect,
    isGuessRepeated,
    shuffleGameData,
} from "../../lib/game-helpers";

import { GameStatusContext } from "../../providers/GameStatus";
import { PuzzleDataContext } from "../../providers/PuzzleData";

function GameControlButtonsPanel({
    shuffledRows,
    setShuffledRows,
    setGridShake,
}) {
    const {
        isGameOver,
        guessCandidate,
        setGuessCandidate,
        submittedGuesses,
        setSubmittedGuesses,
        solvedGameData,
        setSolvedGameData,
    } = React.useContext(GameStatusContext);
    const { gameData, categorySize } = React.useContext(PuzzleDataContext);
    const { toast } = useToast();

    function deselectAll() {
        setGuessCandidate([]);
    }

    function submitCandidateGuess() {
        // check that its a valid guess by size
        if (guessCandidate.length !== categorySize) {
            return;
        }
        // check that the guess hasnt already been submitted previously
        if (isGuessRepeated({ submittedGuesses, guessCandidate })) {
            toast({
                label: "Notification",
                title: "Already guessed",
                description: "You've already tried this combination!",
            });

            return;
        }
        // add guess to state
        setSubmittedGuesses([...submittedGuesses, guessCandidate]);
        // check if the guess is correct
        const {
            isCorrect,
            correctWords,
            correctCategory,
            isGuessOneAway,
            correctDifficulty,
        } = isGuessCorrect({
            guessCandidate,
            gameData,
        });

        // if the guess is correct:
        // set it as solved in game data
        if (isCorrect) {
            setSolvedGameData([
                ...solvedGameData,
                {
                    category: correctCategory,
                    words: correctWords,
                    difficulty: correctDifficulty,
                },
            ]);
            setGuessCandidate([]);
        } else {
            // Shake the grid to give feedback that they were wrong
            setGridShake(true);
            if (isGuessOneAway) {
                toast({
                    label: "Notification",
                    title: "One away!",
                    description: "You're so close!",
                });
            }
        }
    }

    const isSubmitDisabled = isGameOver || guessCandidate.length !== categorySize;

    return (
        <div className="grid grid-cols-3 gap-2 pt-2">
            <Button
                disabled={isGameOver}
                variant="outline"
                className="h-12 rounded-full border-gray-200 hover:bg-gray-50 font-medium"
                onClick={() =>
                    setShuffledRows(shuffleGameData({ gameData: shuffledRows }))
                }
            >
                <Shuffle className="h-4 w-4 mr-2" strokeWidth={2} />
                <span className="select-none text-sm">Shuffle</span>
            </Button>
            <Button
                disabled={isGameOver || guessCandidate.length === 0}
                variant="outline"
                className="h-12 rounded-full border-gray-200 hover:bg-gray-50 font-medium"
                onClick={deselectAll}
            >
                <Undo2 className="h-4 w-4 mr-2" strokeWidth={2} />
                <span className="select-none text-sm">Deselect</span>
            </Button>
            <Button
                onClick={submitCandidateGuess}
                disabled={isSubmitDisabled}
                className={`
                    h-12 rounded-full font-medium transition-all
                    ${isSubmitDisabled
                        ? 'bg-gray-100 text-gray-400 border border-gray-200'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }
                `}
            >
                <Send className="h-4 w-4 mr-2" strokeWidth={2} />
                <span className="select-none text-sm">Submit</span>
            </Button>
        </div>
    );
}

export default GameControlButtonsPanel;
